import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, CardHeader, Table, Btn, Badge, Modal, FormRow, FormField, SearchBar, Spinner } from '../components/ui/Card';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

// ── Constantes ────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'alimentaire',   label: '🍽️ Alimentaire',   subs: ['Céréales & farines','Conserves','Huiles & matières grasses','Boissons','Épices & condiments'] },
  { id: 'medical',       label: '💊 Médical',        subs: ['Médicaments','Matériel de soin','Hygiène & désinfection','Équipements médicaux'] },
  { id: 'bureautique',   label: '🖊️ Bureautique',    subs: ['Papeterie','Informatique','Imprimantes & consommables','Mobilier de bureau'] },
  { id: 'technique',     label: '🔧 Technique',      subs: ['Outillage','Électricité','Plomberie','Maintenance'] },
  { id: 'radio',         label: '📻 Radio & Média',  subs: ['Équipements radio','Câbles & connectique','Antennes','Enregistrement'] },
  { id: 'logistique',    label: '🚛 Logistique',     subs: ['Emballage','Transport','Stockage','Manutention'] },
  { id: 'autre',         label: '📦 Autre',          subs: ['Divers'] },
];
const UNITS = ['pièce','kg','litre','boîte','paquet','mètre','m²','m³','lot','rame','carton'];
const MOTIFS_SORTIE = ['Consommation interne','Activité/Projet','Remis à un service','Don/Don humanitaire','Perte/Détérioration','Autre'];
const MOTIFS_ENTREE = ['Achat','Donation','Retour de prêt','Inventaire (écart positif)','Autre'];

const fmtDate = d => {
  if (!d) return '—';
  const s = String(d).slice(0,10);
  const [y,m,day] = s.split('-').map(Number);
  return new Date(y,m-1,day).toLocaleDateString('fr-FR');
};
const fmtNum = n => parseFloat(n||0).toLocaleString('fr-FR');
const today = () => new Date().toISOString().split('T')[0];
const daysUntil = d => {
  if (!d) return null;
  const diff = new Date(d) - new Date();
  return Math.ceil(diff / 86400000);
};

// ── Niveau d'alerte ───────────────────────────────────────────
const alertLevel = item => {
  const q   = item.current_stock ?? item.quantity ?? 0;
  const min = item.min_stock  ?? 0;
  const saf = item.safety_stock ?? item.min_stock ?? 0;
  if (q <= min)  return 'critical';
  if (q <= saf)  return 'safety';
  return 'ok';
};

// ── Badge alerte ──────────────────────────────────────────────
function AlertBadge({ item }) {
  const lvl = alertLevel(item);
  if (lvl === 'critical') return <Badge type="error">🔴 Critique</Badge>;
  if (lvl === 'safety')   return <Badge type="warning">🟡 Sécurité</Badge>;
  return <Badge type="success">✅ OK</Badge>;
}

// ── Barre de progression stock ────────────────────────────────
function StockBar({ item }) {
  const q   = item.current_stock ?? item.quantity ?? 0;
  const opt = Math.max(item.optimal_stock || item.min_stock * 3 || 10, 1);
  const pct = Math.min(100, (q / opt) * 100);
  const color = alertLevel(item) === 'critical' ? '#dc2626' : alertLevel(item) === 'safety' ? '#f59e0b' : '#16a34a';
  return (
    <div style={{width:80}}>
      <div style={{height:6,background:'#e5e7eb',borderRadius:99,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${pct}%`,background:color,borderRadius:99,transition:'width .3s'}}/>
      </div>
      <p style={{fontSize:'0.65rem',color,fontWeight:700,marginTop:2,textAlign:'center'}}>{q}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ═══════════════════════════════════════════════════════════════
export default function StockPage() {
  const { hasRole, user } = useAuth();
  const canEdit   = hasRole('super_admin','admin','assistant_admin','rh','direction','responsable_stock');
  const canDelete = hasRole('super_admin','admin','responsable_stock');

  const [tab,          setTab]          = useState('catalogue');
  const [items,        setItems]        = useState([]);
  const [movements,    setMovements]    = useState([]);
  const [stats,        setStats]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [mvtLoading,   setMvtLoading]   = useState(false);
  const [search,       setSearch]       = useState('');
  const [filterCat,    setFilterCat]    = useState('');
  const [filterAlert,  setFilterAlert]  = useState('');
  const [modalOpen,    setModalOpen]    = useState(false);
  const [mvtModalOpen, setMvtModalOpen] = useState(false);
  const [detailItem,   setDetailItem]   = useState(null);
  const [invModalOpen, setInvModalOpen] = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [activeArticleTab, setActiveArticleTab] = useState('info');

  const defaultForm = { name:'', category:'', subcategory:'', unit:'pièce', unit_price:0, min_stock:0, safety_stock:0, optimal_stock:0, supplier:'', location:'', description:'', specs:'', photo:'' };
  const defaultMvt  = { type:'entry', article_id:'', quantity:1, unit_price:0, reference_doc:'', invoice_number:'', supplier:'', expiry_date:'', recipient:'', service:'', project:'', reason:'' };
  const defaultInv  = { article_id:'', counted_qty:0, comment:'' };

  const [form,    setForm]    = useState(defaultForm);
  const [mvtForm, setMvtForm] = useState(defaultMvt);
  const [invForm, setInvForm] = useState(defaultInv);

  // ── Chargement ───────────────────────────────────────────────
  const loadItems = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterCat) params.append('category', filterCat);
      if (filterAlert) params.append('alert', filterAlert);
      const r = await api.get(`/stock?${params}`);
      if (!signal?.aborted) setItems(r.data.data || []);
    } catch { if (!signal?.aborted) toast.error('Erreur de chargement'); }
    finally   { if (!signal?.aborted) setLoading(false); }
  }, [search, filterCat, filterAlert]);

  const loadMovements = useCallback(async () => {
    setMvtLoading(true);
    try {
      const r = await api.get('/stock-movements');
      setMovements(r.data.data || []);
    } catch { toast.error('Erreur mouvements'); }
    finally { setMvtLoading(false); }
  }, []);

  const loadStats = useCallback(async () => {
    try { const r = await api.get('/stock/stats'); setStats(r.data); }
    catch {}
  }, []);

  useEffect(() => {
    document.getElementById('page-title') && (document.getElementById('page-title').textContent = 'Gestion du Stock');
    const ctrl = new AbortController();
    loadItems(ctrl.signal);
    loadStats();
    loadMovements();
    return () => ctrl.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ctrl = new AbortController();
    loadItems(ctrl.signal);
    return () => ctrl.abort();
  }, [search, filterCat, filterAlert]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Alertes péremption (30j) ─────────────────────────────────
  const expiryAlerts = useMemo(() => {
    return movements
      .filter(m => m.expiry_date && daysUntil(m.expiry_date) !== null && daysUntil(m.expiry_date) <= 30 && daysUntil(m.expiry_date) >= 0)
      .map(m => ({ ...m, daysLeft: daysUntil(m.expiry_date) }))
      .sort((a,b) => a.daysLeft - b.daysLeft);
  }, [movements]);

  // ── Articles filtrés ─────────────────────────────────────────
  const filteredItems = useMemo(() => {
    let list = items;
    if (filterAlert === 'critical') list = list.filter(i => alertLevel(i) === 'critical');
    if (filterAlert === 'safety')   list = list.filter(i => alertLevel(i) === 'safety');
    return list;
  }, [items, filterAlert]);

  // ── Catégorie courante ────────────────────────────────────────
  const currentCat = CATEGORIES.find(c => c.id === form.category);

  // ── CRUD article ─────────────────────────────────────────────
  const openModal = (item = null) => {
    setEditing(item);
    setActiveArticleTab('info');
    setForm(item ? {
      name:          item.name || '',
      category:      item.category || '',
      subcategory:   item.subcategory || '',
      unit:          item.unit || 'pièce',
      unit_price:    item.unit_price || 0,
      min_stock:     item.min_stock || 0,
      safety_stock:  item.safety_stock || 0,
      optimal_stock: item.optimal_stock || 0,
      supplier:      item.supplier || '',
      location:      item.location || '',
      description:   item.description || '',
      specs:         item.specs || '',
      photo:         item.photo || '',
    } : defaultForm);
    setModalOpen(true);
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await api.put(`/stock/${editing.id}`, form); toast.success('Article mis à jour'); }
      else         { await api.post('/stock', form); toast.success('Article créé'); }
      setModalOpen(false); loadItems(); loadStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Supprimer cet article définitivement ?')) return;
    try { await api.delete(`/stock/${id}`); toast.success('Article supprimé'); loadItems(); loadStats(); }
    catch { toast.error('Erreur'); }
  };

  // ── Mouvement ─────────────────────────────────────────────────
  const openMvt = (item = null) => {
    setMvtForm({ ...defaultMvt, article_id: item?.id || '' });
    setMvtModalOpen(true);
  };

  const handleMouvement = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/stock-movements', mvtForm);
      toast.success(mvtForm.type === 'entry' ? '📥 Entrée enregistrée' : mvtForm.type === 'exit' ? '📤 Sortie enregistrée' : '↔️ Transfert enregistré');
      setMvtModalOpen(false);
      loadItems(); loadStats(); loadMovements();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  // ── Inventaire rapide ─────────────────────────────────────────
  const handleInventaire = async e => {
    e.preventDefault(); setSaving(true);
    try {
      const article = items.find(i => i.id === invForm.article_id);
      if (!article) { toast.error('Article introuvable'); setSaving(false); return; }
      const diff = parseInt(invForm.counted_qty) - (article.current_stock ?? article.quantity ?? 0);
      if (diff === 0) { toast('Aucun écart — stock déjà correct'); setInvModalOpen(false); setSaving(false); return; }
      await api.post('/stock-movements', {
        type:         diff > 0 ? 'entry' : 'exit',
        article_id:   invForm.article_id,
        quantity:     Math.abs(diff),
        reason:       `Inventaire${invForm.comment ? ' — ' + invForm.comment : ''}`,
        reference_doc: `INV-${Date.now()}`,
      });
      toast.success(`Inventaire validé (écart ${diff > 0 ? '+' : ''}${diff})`);
      setInvModalOpen(false); loadItems(); loadStats(); loadMovements();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const f  = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const fm = (k, v) => setMvtForm(prev => ({ ...prev, [k]: v }));
  const fi = (k, v) => setInvForm(prev => ({ ...prev, [k]: v }));

  // ── Colonnes catalogue ────────────────────────────────────────
  const columns = [
    { key:'reference', header:'Code', render:v=>(
      <span style={{fontFamily:'monospace',fontSize:'0.78rem',fontWeight:700,color:'var(--navy)',background:'#f0f4ff',padding:'2px 6px',borderRadius:4}}>{v||'—'}</span>
    )},
    { key:'name', header:'Désignation', render:(v,r)=>(
      <div style={{display:'flex',alignItems:'center',gap:10}}>
        {r.photo
          ? <img src={r.photo} alt="" style={{width:32,height:32,borderRadius:6,objectFit:'cover',flexShrink:0}}/>
          : <div style={{width:32,height:32,borderRadius:6,background:'#f0f4ff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:'1rem'}}>📦</div>
        }
        <div>
          <p style={{fontWeight:600,color:'var(--navy)'}}>{v}</p>
          <p style={{fontSize:'0.7rem',color:'var(--gray-400)'}}>{r.category||''}{r.subcategory?` › ${r.subcategory}`:''}</p>
        </div>
      </div>
    )},
    { key:'current_stock', header:'Quantité', render:(v,r)=><StockBar item={r}/> },
    { key:'unit',          header:'Unité',    render:v=><span style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>{v}</span> },
    { key:'unit_price',    header:'Prix unit.', render:v=><span style={{fontSize:'0.82rem',fontWeight:600}}>{fmtNum(v)} Ar</span> },
    { key:'supplier',      header:'Fournisseur', render:v=><span style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>{v||'—'}</span> },
    { key:'location',      header:'Emplacement', render:v=><span style={{fontSize:'0.78rem'}}>{v||'—'}</span> },
    { key:'id',            header:'Alerte',  render:(_,r)=><AlertBadge item={r}/> },
    { key:'actions',       header:'Actions', render:(_,row)=>(
      <div style={{display:'flex',gap:4}}>
        <Btn size="sm" variant="info"      onClick={()=>setDetailItem(row)}        title="Détail">👁️</Btn>
        {canEdit&&<Btn size="sm" variant="success" onClick={()=>openMvt(row)}       title="Mouvement">⬆⬇</Btn>}
        {canEdit&&<Btn size="sm" variant="secondary" onClick={()=>openModal(row)}>✏️</Btn>}
        {canDelete&&<Btn size="sm" variant="danger"    onClick={()=>handleDelete(row.id)}>🗑️</Btn>}
      </div>
    )},
  ];

  // ── Colonnes mouvements ───────────────────────────────────────
  const mvtColumns = [
    { key:'created_at',    header:'Date',    render:v=>fmtDate(v) },
    { key:'type',          header:'Type',    render:v=>(
      v==='entree'||v==='entry'
        ? <Badge type="success">📥 Entrée</Badge>
        : v==='sortie'||v==='exit'
          ? <Badge type="error">📤 Sortie</Badge>
          : <Badge type="info">↔️ Transfert</Badge>
    )},
    { key:'stock_articles', header:'Article', render:(v,r)=><span style={{fontWeight:600}}>{v?.name||r.article_name||'—'}</span> },
    { key:'quantity',      header:'Qté',     render:v=><span style={{fontWeight:700}}>{v}</span> },
    { key:'reference_doc', header:'Référence', render:v=><span style={{fontFamily:'monospace',fontSize:'0.78rem'}}>{v||'—'}</span> },
    { key:'reason',        header:'Motif',   render:v=><span style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>{v||'—'}</span> },
  ];

  // ═══════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="animate-fade">

      {/* ── KPI ─────────────────────────────────────────────── */}
      {stats && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(145px,1fr))',gap:12,marginBottom:18}}>
          {[
            { label:'Total articles',   value:stats.total,       color:'var(--navy)',    icon:'📦' },
            { label:'Stock critique',   value:stats.critical,    color:'#dc2626',        icon:'🔴' },
            { label:'Stock sécurité',   value:stats.safety,      color:'#f59e0b',        icon:'🟡' },
            { label:'Péremptions <30j', value:expiryAlerts.length, color:'#7c3aed',     icon:'⚠️' },
            { label:'Valeur totale',    value:`${fmtNum(stats.totalValue)} Ar`, color:'#16a34a', icon:'💰' },
          ].map(s=>(
            <div key={s.label} style={{background:'white',borderRadius:'var(--radius)',boxShadow:'var(--shadow)',padding:'14px 18px',borderLeft:`4px solid ${s.color}`}}>
              <p style={{fontSize:'0.7rem',color:'var(--gray-400)',marginBottom:4}}>{s.icon} {s.label}</p>
              <p style={{fontSize:'1.5rem',fontWeight:700,color:s.color,lineHeight:1}}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── En-tête + Tabs ──────────────────────────────────── */}
      <div style={{background:'white',borderRadius:'var(--radius)',boxShadow:'var(--shadow)',marginBottom:16,overflow:'hidden'}}>
        <div style={{padding:'16px 24px 0'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8,marginBottom:12}}>
            <div>
              <h2 style={{fontFamily:"'Playfair Display',serif",color:'var(--navy)',fontSize:'1.2rem',fontWeight:700}}>Gestion du Stock</h2>
              <p style={{fontSize:'0.78rem',color:'var(--gray-400)',marginTop:2}}>{items.length} article(s) — {movements.length} mouvement(s)</p>
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {canEdit&&<Btn variant="secondary" onClick={()=>{ setInvForm(defaultInv); setInvModalOpen(true); }}>📋 Inventaire</Btn>}
              {canEdit&&<Btn variant="secondary" onClick={()=>openMvt()}>⬆⬇ Mouvement</Btn>}
              {canEdit&&<Btn variant="gold"      onClick={()=>openModal()}>➕ Nouvel article</Btn>}
            </div>
          </div>
        </div>
        <div style={{display:'flex',borderTop:'1px solid var(--gray-100)',overflowX:'auto'}}>
          {[
            {id:'catalogue',  label:'📦 Catalogue'},
            {id:'mouvements', label:'🔄 Mouvements'},
            {id:'alertes',    label:`⚠️ Alertes${(stats?.critical||0)+(expiryAlerts.length)>0?` (${(stats?.critical||0)+(expiryAlerts.length)})`:''}` },
            {id:'inventaire', label:'📋 Inventaire'},
          ].map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:'10px 20px',border:'none',background:'transparent',cursor:'pointer',
                fontSize:'0.82rem',fontWeight:600,whiteSpace:'nowrap',
                color:tab===t.id?'var(--navy)':'var(--gray-400)',
                borderBottom:tab===t.id?'2px solid var(--gold)':'2px solid transparent',marginBottom:'-1px'}}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══ TAB CATALOGUE ═══════════════════════════════════════ */}
      {tab==='catalogue'&&(
        <Card>
          <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:16}}>
            <div style={{flex:1,minWidth:180}}>
              <SearchBar value={search} onChange={setSearch} placeholder="Code, désignation, fournisseur..."/>
            </div>
            <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{width:'auto',minWidth:160}}>
              <option value="">Toutes catégories</option>
              {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <select value={filterAlert} onChange={e=>setFilterAlert(e.target.value)} style={{width:'auto'}}>
              <option value="">Tous niveaux</option>
              <option value="critical">🔴 Critique</option>
              <option value="safety">🟡 Sécurité</option>
            </select>
          </div>
          {loading ? <Spinner/> : <Table columns={columns} data={filteredItems} emptyMessage="Aucun article trouvé"/>}
        </Card>
      )}

      {/* ══ TAB MOUVEMENTS ══════════════════════════════════════ */}
      {tab==='mouvements'&&(
        <Card>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
            {canEdit&&<Btn variant="gold" onClick={()=>openMvt()}>➕ Nouveau mouvement</Btn>}
          </div>
          {mvtLoading ? <Spinner/> : <Table columns={mvtColumns} data={movements} emptyMessage="Aucun mouvement enregistré"/>}
        </Card>
      )}

      {/* ══ TAB ALERTES ═════════════════════════════════════════ */}
      {tab==='alertes'&&(
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          {/* Alertes stock critique */}
          <Card>
            <CardHeader title="🔴 Stock critique" subtitle="Articles en dessous du stock minimum"/>
            {items.filter(i=>alertLevel(i)==='critical').length===0
              ? <p style={{color:'var(--gray-400)',fontStyle:'italic'}}>Aucun article en stock critique</p>
              : items.filter(i=>alertLevel(i)==='critical').map(item=>(
                <div key={item.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',borderRadius:10,border:'1.5px solid #fee2e2',background:'#fff5f5',marginBottom:8,flexWrap:'wrap',gap:8}}>
                  <div>
                    <p style={{fontWeight:700,color:'#991b1b'}}>{item.name}</p>
                    <p style={{fontSize:'0.72rem',color:'var(--gray-400)'}}>{item.reference} • {item.category||'Sans catégorie'} • {item.location||'—'}</p>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <div style={{textAlign:'center'}}>
                      <p style={{fontWeight:700,fontSize:'1.3rem',color:'#dc2626',lineHeight:1}}>{item.current_stock??item.quantity??0}</p>
                      <p style={{fontSize:'0.65rem',color:'var(--gray-400)'}}>en stock</p>
                    </div>
                    <div style={{textAlign:'center'}}>
                      <p style={{fontWeight:700,color:'#6b7280'}}>{item.min_stock??0}</p>
                      <p style={{fontSize:'0.65rem',color:'var(--gray-400)'}}>minimum</p>
                    </div>
                    {canEdit&&<Btn size="sm" variant="success" onClick={()=>{fm('article_id',item.id);fm('type','entry');setMvtModalOpen(true);}}>📥 Commander</Btn>}
                  </div>
                </div>
              ))
            }
          </Card>

          {/* Alertes sécurité */}
          <Card>
            <CardHeader title="🟡 Stock de sécurité" subtitle="Articles entre le minimum et le seuil de sécurité"/>
            {items.filter(i=>alertLevel(i)==='safety').length===0
              ? <p style={{color:'var(--gray-400)',fontStyle:'italic'}}>Aucun article en alerte sécurité</p>
              : items.filter(i=>alertLevel(i)==='safety').map(item=>(
                <div key={item.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',borderRadius:10,border:'1.5px solid #fde68a',background:'#fffbeb',marginBottom:8,flexWrap:'wrap',gap:8}}>
                  <div>
                    <p style={{fontWeight:700,color:'#854d0e'}}>{item.name}</p>
                    <p style={{fontSize:'0.72rem',color:'var(--gray-400)'}}>{item.reference} • {item.supplier||'—'}</p>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <div style={{textAlign:'center'}}>
                      <p style={{fontWeight:700,fontSize:'1.3rem',color:'#f59e0b',lineHeight:1}}>{item.current_stock??item.quantity??0}</p>
                      <p style={{fontSize:'0.65rem',color:'var(--gray-400)'}}>en stock</p>
                    </div>
                    <div style={{textAlign:'center'}}>
                      <p style={{fontWeight:700,color:'#6b7280'}}>{item.safety_stock??0}</p>
                      <p style={{fontSize:'0.65rem',color:'var(--gray-400)'}}>sécurité</p>
                    </div>
                  </div>
                </div>
              ))
            }
          </Card>

          {/* Alertes péremption */}
          <Card>
            <CardHeader title="⏰ Péremptions proches" subtitle="Articles expirant dans les 30 jours"/>
            {expiryAlerts.length===0
              ? <p style={{color:'var(--gray-400)',fontStyle:'italic'}}>Aucune péremption imminente</p>
              : expiryAlerts.map((m,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',borderRadius:10,border:`1.5px solid ${m.daysLeft<=7?'#fee2e2':'#fde68a'}`,background:m.daysLeft<=7?'#fff5f5':'#fffbeb',marginBottom:8,flexWrap:'wrap',gap:8}}>
                  <div>
                    <p style={{fontWeight:700,color:m.daysLeft<=7?'#991b1b':'#854d0e'}}>{m.stock_articles?.name||m.article_name||'—'}</p>
                    <p style={{fontSize:'0.72rem',color:'var(--gray-400)'}}>Réf. doc: {m.reference_doc||'—'} • {m.quantity} {m.unit||'unité(s)'}</p>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <p style={{fontWeight:700,color:m.daysLeft<=7?'#dc2626':'#f59e0b'}}>{m.daysLeft} jour(s)</p>
                    <p style={{fontSize:'0.7rem',color:'var(--gray-400)'}}>Exp. le {fmtDate(m.expiry_date)}</p>
                  </div>
                </div>
              ))
            }
          </Card>
        </div>
      )}

      {/* ══ TAB INVENTAIRE ══════════════════════════════════════ */}
      {tab==='inventaire'&&(
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <Card>
            <CardHeader title="📋 État de l'inventaire" subtitle="Valeur et quantité de chaque article"
              action={canEdit&&<Btn variant="gold" onClick={()=>{ setInvForm(defaultInv); setInvModalOpen(true); }}>📝 Saisir inventaire</Btn>}/>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.82rem'}}>
                <thead>
                  <tr style={{background:'#f8fafc'}}>
                    {['Code','Désignation','Catégorie','Qté actuelle','Stock min','Valeur stock','Statut'].map(h=>(
                      <th key={h} style={{padding:'10px 12px',textAlign:'left',fontWeight:700,color:'var(--navy)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.04em',borderBottom:'2px solid var(--gray-100)'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item,idx)=>{
                    const q = item.current_stock??item.quantity??0;
                    const valeur = q * (item.unit_price||0);
                    return (
                      <tr key={item.id} style={{background:idx%2===0?'white':'#fafafa',borderBottom:'1px solid var(--gray-100)'}}>
                        <td style={{padding:'8px 12px',fontFamily:'monospace',fontSize:'0.78rem',color:'var(--navy)',fontWeight:700}}>{item.reference||'—'}</td>
                        <td style={{padding:'8px 12px',fontWeight:600}}>{item.name}</td>
                        <td style={{padding:'8px 12px',color:'var(--gray-500)',fontSize:'0.78rem'}}>{item.category||'—'}</td>
                        <td style={{padding:'8px 12px',textAlign:'center'}}>
                          <span style={{fontWeight:700,color:alertLevel(item)==='critical'?'#dc2626':alertLevel(item)==='safety'?'#f59e0b':'#16a34a'}}>{q}</span>
                          <span style={{fontSize:'0.7rem',color:'var(--gray-400)',marginLeft:4}}>{item.unit}</span>
                        </td>
                        <td style={{padding:'8px 12px',textAlign:'center',color:'var(--gray-500)'}}>{item.min_stock??0}</td>
                        <td style={{padding:'8px 12px',fontWeight:600,color:'var(--navy)'}}>{fmtNum(valeur)} Ar</td>
                        <td style={{padding:'8px 12px'}}><AlertBadge item={item}/></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{background:'#f0f4ff',fontWeight:700}}>
                    <td colSpan={5} style={{padding:'10px 12px',color:'var(--navy)'}}>TOTAL</td>
                    <td style={{padding:'10px 12px',color:'var(--navy)'}}>
                      {fmtNum(items.reduce((s,i)=>{
                        const q = i.current_stock??i.quantity??0;
                        return s + q*(i.unit_price||0);
                      },0))} Ar
                    </td>
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          MODAL ARTICLE
      ═══════════════════════════════════════════════════════ */}
      <Modal open={modalOpen} onClose={()=>setModalOpen(false)}
        title={editing?`✏️ Modifier — ${editing.name}`:'➕ Nouvel article'}
        width={680}>
        <form onSubmit={handleSave}>
          {/* Onglets article */}
          <div style={{display:'flex',borderBottom:'2px solid var(--gray-100)',padding:'0 24px'}}>
            {[{id:'info',label:'📋 Informations'},{id:'stock',label:'📊 Seuils & Stock'},{id:'fournisseur',label:'🏭 Fournisseur'}].map(t=>(
              <button key={t.id} type="button" onClick={()=>setActiveArticleTab(t.id)}
                style={{padding:'9px 16px',border:'none',background:'transparent',cursor:'pointer',fontSize:'0.78rem',fontWeight:600,whiteSpace:'nowrap',
                  color:activeArticleTab===t.id?'var(--navy)':'var(--gray-400)',
                  borderBottom:activeArticleTab===t.id?'2px solid var(--gold)':'2px solid transparent',marginBottom:'-2px'}}>
                {t.label}
              </button>
            ))}
          </div>

          <div style={{padding:'18px 24px',minHeight:220}}>
            {activeArticleTab==='info'&&<>
              <FormRow cols={1}>
                <FormField label="Désignation *">
                  <input value={form.name} onChange={e=>f('name',e.target.value)} required placeholder="Nom complet de l'article"/>
                </FormField>
              </FormRow>
              <FormRow>
                <FormField label="Catégorie">
                  <select value={form.category} onChange={e=>{ f('category',e.target.value); f('subcategory',''); }}>
                    <option value="">— Sélectionner —</option>
                    {CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </FormField>
                <FormField label="Sous-catégorie">
                  <select value={form.subcategory} onChange={e=>f('subcategory',e.target.value)} disabled={!currentCat}>
                    <option value="">— Sélectionner —</option>
                    {(currentCat?.subs||[]).map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </FormField>
              </FormRow>
              <FormRow>
                <FormField label="Unité de mesure">
                  <select value={form.unit} onChange={e=>f('unit',e.target.value)}>
                    {UNITS.map(u=><option key={u}>{u}</option>)}
                  </select>
                </FormField>
                <FormField label="Prix unitaire (Ar)">
                  <input type="number" min="0" value={form.unit_price} onChange={e=>f('unit_price',e.target.value)}/>
                </FormField>
              </FormRow>
              <FormRow cols={1}>
                <FormField label="Emplacement physique">
                  <input value={form.location} onChange={e=>f('location',e.target.value)} placeholder="Ex: Étagère A3, Dépôt principal..."/>
                </FormField>
              </FormRow>
              <FormRow cols={1}>
                <FormField label="Description / Caractéristiques techniques">
                  <textarea value={form.description} onChange={e=>f('description',e.target.value)} rows={3} style={{resize:'vertical'}} placeholder="Caractéristiques, dimensions, références techniques..."/>
                </FormField>
              </FormRow>
              <FormRow cols={1}>
                <FormField label="URL photo (optionnelle)">
                  <input value={form.photo} onChange={e=>f('photo',e.target.value)} placeholder="https://..."/>
                </FormField>
              </FormRow>
            </>}

            {activeArticleTab==='stock'&&<>
              <p style={{fontSize:'0.78rem',color:'var(--gray-400)',marginBottom:14}}>
                Configurez les seuils d'alerte. Le système déclenchera des alertes automatiquement selon ces valeurs.
              </p>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:14}}>
                <div style={{padding:14,borderRadius:10,border:'2px solid #fee2e2',background:'#fff5f5'}}>
                  <p style={{fontSize:'0.72rem',fontWeight:700,color:'#991b1b',marginBottom:8}}>🔴 Stock minimum</p>
                  <p style={{fontSize:'0.68rem',color:'var(--gray-400)',marginBottom:8}}>Déclenchement alerte rouge</p>
                  <input type="number" min="0" value={form.min_stock} onChange={e=>f('min_stock',e.target.value)}
                    style={{width:'100%',textAlign:'center',fontWeight:700,fontSize:'1.1rem',color:'#dc2626',border:'1.5px solid #fee2e2',borderRadius:6,padding:'6px'}}/>
                  <p style={{fontSize:'0.65rem',color:'var(--gray-400)',textAlign:'center',marginTop:4}}>{form.unit||'unité(s)'}</p>
                </div>
                <div style={{padding:14,borderRadius:10,border:'2px solid #fde68a',background:'#fffbeb'}}>
                  <p style={{fontSize:'0.72rem',fontWeight:700,color:'#854d0e',marginBottom:8}}>🟡 Stock de sécurité</p>
                  <p style={{fontSize:'0.68rem',color:'var(--gray-400)',marginBottom:8}}>Déclenchement alerte orange</p>
                  <input type="number" min="0" value={form.safety_stock} onChange={e=>f('safety_stock',e.target.value)}
                    style={{width:'100%',textAlign:'center',fontWeight:700,fontSize:'1.1rem',color:'#f59e0b',border:'1.5px solid #fde68a',borderRadius:6,padding:'6px'}}/>
                  <p style={{fontSize:'0.65rem',color:'var(--gray-400)',textAlign:'center',marginTop:4}}>{form.unit||'unité(s)'}</p>
                </div>
                <div style={{padding:14,borderRadius:10,border:'2px solid #bbf7d0',background:'#f0fdf4'}}>
                  <p style={{fontSize:'0.72rem',fontWeight:700,color:'#166534',marginBottom:8}}>✅ Stock optimal</p>
                  <p style={{fontSize:'0.68rem',color:'var(--gray-400)',marginBottom:8}}>Niveau de référence</p>
                  <input type="number" min="0" value={form.optimal_stock} onChange={e=>f('optimal_stock',e.target.value)}
                    style={{width:'100%',textAlign:'center',fontWeight:700,fontSize:'1.1rem',color:'#16a34a',border:'1.5px solid #bbf7d0',borderRadius:6,padding:'6px'}}/>
                  <p style={{fontSize:'0.65rem',color:'var(--gray-400)',textAlign:'center',marginTop:4}}>{form.unit||'unité(s)'}</p>
                </div>
              </div>
              {form.min_stock && form.safety_stock && parseInt(form.safety_stock) < parseInt(form.min_stock) && (
                <p style={{color:'#dc2626',fontSize:'0.78rem',marginTop:10}}>⚠️ Le stock de sécurité doit être supérieur au stock minimum.</p>
              )}
            </>}

            {activeArticleTab==='fournisseur'&&<>
              <FormRow cols={1}>
                <FormField label="Fournisseur principal">
                  <input value={form.supplier} onChange={e=>f('supplier',e.target.value)} placeholder="Nom du fournisseur..."/>
                </FormField>
              </FormRow>
              <FormRow cols={1}>
                <FormField label="Spécifications techniques">
                  <textarea value={form.specs} onChange={e=>f('specs',e.target.value)} rows={4} style={{resize:'vertical'}} placeholder="Référence fabricant, norme, dimensions, poids..."/>
                </FormField>
              </FormRow>
            </>}
          </div>

          <div style={{display:'flex',gap:10,justifyContent:'flex-end',padding:'12px 24px',borderTop:'1px solid var(--gray-100)',background:'var(--gray-50)'}}>
            <Btn type="button" variant="secondary" onClick={()=>setModalOpen(false)}>Annuler</Btn>
            <Btn type="submit" variant="gold" disabled={saving}>{saving?'Enregistrement...':editing?'💾 Mettre à jour':'➕ Créer'}</Btn>
          </div>
        </form>
      </Modal>

      {/* ═══════════════════════════════════════════════════════
          MODAL MOUVEMENT
      ═══════════════════════════════════════════════════════ */}
      <Modal open={mvtModalOpen} onClose={()=>setMvtModalOpen(false)}
        title={mvtForm.type==='entry'?'📥 Entrée de stock':mvtForm.type==='exit'?'📤 Sortie de stock':'↔️ Transfert de stock'}
        width={620}>
        <form onSubmit={handleMouvement} style={{padding:24}}>
          {/* Sélecteur de type */}
          <div style={{display:'flex',gap:8,marginBottom:18}}>
            {[{v:'entry',label:'📥 Entrée',color:'#16a34a'},{v:'exit',label:'📤 Sortie',color:'#dc2626'},{v:'transfer',label:'↔️ Transfert',color:'#0891b2'}].map(t=>(
              <button key={t.v} type="button" onClick={()=>fm('type',t.v)}
                style={{flex:1,padding:'10px',border:`2px solid ${mvtForm.type===t.v?t.color:'var(--gray-200)'}`,borderRadius:10,cursor:'pointer',
                  background:mvtForm.type===t.v?`${t.color}18`:'white',fontWeight:700,fontSize:'0.82rem',color:mvtForm.type===t.v?t.color:'var(--gray-500)'}}>
                {t.label}
              </button>
            ))}
          </div>

          <FormRow cols={1}>
            <FormField label="Article *">
              <select value={mvtForm.article_id} onChange={e=>fm('article_id',e.target.value)} required>
                <option value="">— Sélectionner un article —</option>
                {items.map(i=><option key={i.id} value={i.id}>{i.reference} — {i.name} (Qté: {i.current_stock??i.quantity??0} {i.unit})</option>)}
              </select>
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="Quantité *">
              <input type="number" min="1" value={mvtForm.quantity} onChange={e=>fm('quantity',e.target.value)} required/>
            </FormField>
            <FormField label="Prix unitaire (Ar)">
              <input type="number" min="0" value={mvtForm.unit_price} onChange={e=>fm('unit_price',e.target.value)}/>
            </FormField>
          </FormRow>

          {/* Champs spécifiques ENTRÉE */}
          {mvtForm.type==='entry'&&<>
            <FormRow>
              <FormField label="N° Bon de livraison">
                <input value={mvtForm.reference_doc} onChange={e=>fm('reference_doc',e.target.value)} placeholder="BL-2024-001"/>
              </FormField>
              <FormField label="N° Facture">
                <input value={mvtForm.invoice_number} onChange={e=>fm('invoice_number',e.target.value)} placeholder="FAC-2024-001"/>
              </FormField>
            </FormRow>
            <FormRow>
              <FormField label="Fournisseur">
                <input value={mvtForm.supplier} onChange={e=>fm('supplier',e.target.value)}/>
              </FormField>
              <FormField label="Date de péremption">
                <input type="date" value={mvtForm.expiry_date} onChange={e=>fm('expiry_date',e.target.value)}/>
              </FormField>
            </FormRow>
            <FormRow cols={1}>
              <FormField label="Motif / Observation">
                <select value={mvtForm.reason} onChange={e=>fm('reason',e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {MOTIFS_ENTREE.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </FormField>
            </FormRow>
          </>}

          {/* Champs spécifiques SORTIE */}
          {mvtForm.type==='exit'&&<>
            <FormRow>
              <FormField label="N° Bon de sortie *">
                <input value={mvtForm.reference_doc} onChange={e=>fm('reference_doc',e.target.value)} placeholder="BS-2024-001" required/>
              </FormField>
              <FormField label="Destinataire (personne/service)">
                <input value={mvtForm.recipient} onChange={e=>fm('recipient',e.target.value)}/>
              </FormField>
            </FormRow>
            <FormRow cols={1}>
              <FormField label="Motif *">
                <select value={mvtForm.reason} onChange={e=>fm('reason',e.target.value)} required>
                  <option value="">— Sélectionner —</option>
                  {MOTIFS_SORTIE.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </FormField>
            </FormRow>
            {mvtForm.article_id&&(()=>{
              const art = items.find(i=>i.id===mvtForm.article_id);
              const dispo = art ? (art.current_stock??art.quantity??0) : 0;
              const demanded = parseInt(mvtForm.quantity)||0;
              if (demanded > dispo) return (
                <p style={{color:'#dc2626',fontSize:'0.78rem',padding:'8px 12px',background:'#fff5f5',borderRadius:8,border:'1px solid #fee2e2'}}>
                  ⚠️ Stock insuffisant : {dispo} {art?.unit||'unité(s)'} disponible(s), vous en demandez {demanded}.
                </p>
              );
              return null;
            })()}
          </>}

          {/* Champs spécifiques TRANSFERT */}
          {mvtForm.type==='transfer'&&<>
            <FormRow>
              <FormField label="De (emplacement)">
                <input value={mvtForm.service} onChange={e=>fm('service',e.target.value)} placeholder="Dépôt A"/>
              </FormField>
              <FormField label="Vers (emplacement)">
                <input value={mvtForm.project} onChange={e=>fm('project',e.target.value)} placeholder="Dépôt B"/>
              </FormField>
            </FormRow>
            <FormRow cols={1}>
              <FormField label="Référence / Motif">
                <input value={mvtForm.reference_doc} onChange={e=>fm('reference_doc',e.target.value)}/>
              </FormField>
            </FormRow>
          </>}

          <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:12}}>
            <Btn type="button" variant="secondary" onClick={()=>setMvtModalOpen(false)}>Annuler</Btn>
            <Btn type="submit" variant="gold" disabled={saving}>{saving?'Enregistrement...':'✅ Valider le mouvement'}</Btn>
          </div>
        </form>
      </Modal>

      {/* ═══════════════════════════════════════════════════════
          MODAL INVENTAIRE
      ═══════════════════════════════════════════════════════ */}
      <Modal open={invModalOpen} onClose={()=>setInvModalOpen(false)} title="📋 Saisie d'inventaire" width={520}>
        <form onSubmit={handleInventaire} style={{padding:24}}>
          <p style={{fontSize:'0.82rem',color:'var(--gray-500)',marginBottom:16}}>
            Saisissez la quantité comptée physiquement. L'écart sera ajusté automatiquement via un mouvement d'inventaire.
          </p>
          <FormRow cols={1}>
            <FormField label="Article *">
              <select value={invForm.article_id} onChange={e=>fi('article_id',e.target.value)} required>
                <option value="">— Sélectionner un article —</option>
                {items.map(i=><option key={i.id} value={i.id}>{i.reference} — {i.name} (stock: {i.current_stock??i.quantity??0} {i.unit})</option>)}
              </select>
            </FormField>
          </FormRow>
          {invForm.article_id&&(()=>{
            const art = items.find(i=>i.id===invForm.article_id);
            const diff = parseInt(invForm.counted_qty||0) - (art?.current_stock??art?.quantity??0);
            return (
              <div style={{background:'#f8fafc',borderRadius:10,padding:'12px 16px',marginBottom:12,border:'1px solid var(--gray-200)'}}>
                <p style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>Stock système : <strong>{art?.current_stock??art?.quantity??0} {art?.unit}</strong></p>
                {invForm.counted_qty!==''&&invForm.counted_qty!==0&&(
                  <p style={{fontSize:'0.78rem',marginTop:4,color:diff===0?'#16a34a':diff>0?'#0891b2':'#dc2626',fontWeight:700}}>
                    Écart : {diff>0?'+':''}{diff} {art?.unit} ({diff===0?'Aucun écart':diff>0?'Gain':'Perte'})
                  </p>
                )}
              </div>
            );
          })()}
          <FormRow>
            <FormField label="Quantité comptée *">
              <input type="number" min="0" value={invForm.counted_qty} onChange={e=>fi('counted_qty',e.target.value)} required/>
            </FormField>
          </FormRow>
          <FormRow cols={1}>
            <FormField label="Commentaire / Justificatif">
              <input value={invForm.comment} onChange={e=>fi('comment',e.target.value)} placeholder="Motif de l'écart..."/>
            </FormField>
          </FormRow>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:8}}>
            <Btn type="button" variant="secondary" onClick={()=>setInvModalOpen(false)}>Annuler</Btn>
            <Btn type="submit" variant="gold" disabled={saving}>{saving?'Enregistrement...':'✅ Valider l\'inventaire'}</Btn>
          </div>
        </form>
      </Modal>

      {/* ═══════════════════════════════════════════════════════
          MODAL DÉTAIL ARTICLE
      ═══════════════════════════════════════════════════════ */}
      {detailItem&&(
        <Modal open={!!detailItem} onClose={()=>setDetailItem(null)} title={`📦 ${detailItem.name}`} width={560}>
          <div style={{padding:24}}>
            <div style={{display:'flex',gap:16,marginBottom:20,alignItems:'flex-start'}}>
              {detailItem.photo
                ? <img src={detailItem.photo} alt="" style={{width:80,height:80,borderRadius:12,objectFit:'cover',flexShrink:0}}/>
                : <div style={{width:80,height:80,borderRadius:12,background:'#f0f4ff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:'2rem'}}>📦</div>
              }
              <div>
                <p style={{fontWeight:700,color:'var(--navy)',fontSize:'1.05rem'}}>{detailItem.name}</p>
                <p style={{fontSize:'0.78rem',color:'var(--gray-400)',marginTop:2}}>{detailItem.reference}</p>
                <p style={{fontSize:'0.78rem',color:'var(--gray-400)'}}>{detailItem.category||''}{detailItem.subcategory?` › ${detailItem.subcategory}`:''}</p>
                <div style={{marginTop:8}}><AlertBadge item={detailItem}/></div>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              {[
                ['Quantité',`${detailItem.current_stock??detailItem.quantity??0} ${detailItem.unit||''}`],
                ['Prix unitaire',`${fmtNum(detailItem.unit_price)} Ar`],
                ['Stock minimum',`${detailItem.min_stock||0} ${detailItem.unit||''}`],
                ['Stock sécurité',`${detailItem.safety_stock||0} ${detailItem.unit||''}`],
                ['Fournisseur',detailItem.supplier||'—'],
                ['Emplacement',detailItem.location||'—'],
              ].map(([k,v])=>(
                <div key={k} style={{background:'#f8fafc',borderRadius:8,padding:'8px 12px'}}>
                  <p style={{fontSize:'0.65rem',fontWeight:700,color:'var(--gray-400)',textTransform:'uppercase',letterSpacing:'0.05em'}}>{k}</p>
                  <p style={{fontWeight:600,color:'var(--navy)',marginTop:2}}>{v}</p>
                </div>
              ))}
            </div>
            {detailItem.description&&<div style={{marginTop:12,padding:'10px 14px',background:'#f8fafc',borderRadius:8}}>
              <p style={{fontSize:'0.7rem',fontWeight:700,color:'var(--gray-400)',textTransform:'uppercase',marginBottom:4}}>Description</p>
              <p style={{fontSize:'0.82rem',color:'var(--navy)'}}>{detailItem.description}</p>
            </div>}
            {canEdit&&<div style={{display:'flex',gap:8,marginTop:16,justifyContent:'flex-end'}}>
              <Btn variant="success" onClick={()=>{setDetailItem(null);openMvt(detailItem);}}>⬆⬇ Mouvement</Btn>
              <Btn variant="secondary" onClick={()=>{setDetailItem(null);openModal(detailItem);}}>✏️ Modifier</Btn>
            </div>}
          </div>
        </Modal>
      )}
    </div>
  );
}
